import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import {
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TrashIcon } from 'src/components/therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import {
  createAnnouncementAction,
  resetCreateAnnouncementAction,
  resetUpdateAnnouncementAction,
  updateAnnouncementAction,
} from 'src/redux/actions/schedularAnnouncementsAction';

type props = {
  date: any;
  handleCloseDialog: any;
  handleOpenDeleteAnnounceModal: any;
};

const AnnouncementDialog = (props: props) => {
  const { handleCloseDialog, date, handleOpenDeleteAnnounceModal } = props;
  const dispatch = useDispatch();
  const [mode, setMode] = useState('ADD');
  const [announcements, setAnnouncements] = useState<any>([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<any>();
  const [popUpMessage, setPopUpMessage] = useState({ type: '', message: '' });
  const [currentIndex, setCurrentIndex] = useState(0);
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );

  const getAllAnnouncements = useSelector((state: any) => state?.getAllAnnouncementReducer);
  const createAnnouncement = useSelector((state: any) => state?.createAnnouncementReducer);
  const updateAnnouncement = useSelector((state: any) => state?.updateAnnouncementReducer);

  useEffect(() => {
    if (getAllAnnouncements?.response?.data) {
      setAnnouncements([{ announce: '' }, ...getAllAnnouncements.response.data]);
    }
  }, [getAllAnnouncements]);

  useEffect(() => {
    if (!createAnnouncement?.response) return;
    if (createAnnouncement?.response !== null && createAnnouncement?.response?.code !== 'OK') {
      setPopUpMessage({ message: createAnnouncement?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);
      dispatch(resetCreateAnnouncementAction());
      return;
    }
    setSelectedAnnouncement({ announce: '' });
    setPopUpMessage({ message: createAnnouncement?.response?.message, type: '' });
    setTimeout(() => {
      setPopUpMessage({ message: '', type: '' });
    }, 3000);
    dispatch(resetCreateAnnouncementAction());
  }, [createAnnouncement]);

  useEffect(() => {
    if (!updateAnnouncement?.response) return;
    if (updateAnnouncement?.response !== null && updateAnnouncement?.response?.code !== 'OK') {
      setPopUpMessage({ message: updateAnnouncement?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);
      dispatch(resetUpdateAnnouncementAction());
      return;
    }
    setPopUpMessage({ message: updateAnnouncement?.response?.message, type: '' });
    setMode('VIEW');
    setTimeout(() => {
      setPopUpMessage({ message: '', type: '' });
    }, 3000);
    dispatch(resetUpdateAnnouncementAction());
  }, [updateAnnouncement]);

  const handleNext = () => {
    setMode('VIEW');
    setCurrentIndex((prev) => prev + 1);
    setSelectedAnnouncement(announcements[currentIndex + 1]);
  };
  const handlePrevious = () => {
    if (currentIndex === 1) setMode('ADD');
    else setMode('VIEW');
    setCurrentIndex((prev) => prev - 1);
    setSelectedAnnouncement(announcements[currentIndex - 1]);
  };

  const handleSaveAnnouncement = () => {
    if (mode === 'VIEW') {
      setMode('EDIT');
    } else if (mode === 'EDIT') {
      dispatch(
        updateAnnouncementAction({
          date: moment(date).format('YYYY-MM-DD'),
          announcement: selectedAnnouncement?.announce,
          uuid: selectedAnnouncement?.uuid,
          providerId: providerId
        })
      );
      setMode('VIEW');
    } else {
      dispatch(
        createAnnouncementAction({
          date: moment(date).format('YYYY-MM-DD'),
          announcement: selectedAnnouncement?.announce,
          providerId: providerId
        })
      );
    }
  };

  return (
    <>
      <Box>
        <DialogContent>
          <Stack
            flexDirection={'column'}
            rowGap={2}
          >
            <Stack
              sx={{ display: 'flex', textAlign: 'center' }}
              mb={1}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'10px'}
                >
                  <Typography variant="h6">
                    Team Announcement for {moment(date).format('MMM D, YYYY')}
                  </Typography>
                </Box>
              </Box>
            </Stack>
            {getAllAnnouncements?.loading ? (
              <Skeleton
                variant="rounded"
                height="120px"
              />
            ) : (
              <Stack spacing={2}>
                {mode === 'VIEW' ? (
                  <Stack sx={{ width: '552px', border: '1px solid #D2D6DB', borderRadius: '10px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        padding: '9px 12px 8px 12px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '3px',
                        alignSelf: 'stretch',
                        minHeight: '110px',
                        overflow: 'visible',
                      }}
                    >
                      <Typography
                        sx={{
                          color: 'var(--text-secondary, #6C737F)',
                          fontFamily: 'Inter',
                          fontSize: '11px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: '12px',
                          letterSpacing: '0.15px',
                        }}
                      >
                        DOR Announcement
                      </Typography>
                      <Typography
                        flex="1 0 0"
                        sx={{
                          color: 'var(--text-primary, #111927)',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          lineHeight: '24px',
                          letterSpacing: '0.15px',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                        }}
                      >
                        {selectedAnnouncement?.announce}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Stack>
                    <TextField
                      id="standard-basic"
                      label="DOR Announcement"
                      multiline
                      minRows={3}
                      value={selectedAnnouncement?.announce}
                      onChange={(e) => {
                        setSelectedAnnouncement((prevState: any) => ({
                          ...prevState,
                          announce: e.target.value,
                        }));
                      }}
                      error={selectedAnnouncement?.announce?.length > 400}
                      helperText={
                        selectedAnnouncement?.announce?.length > 400
                          ? `The input exceeds the maximum character limit of ${400}. Please reduce the text length.`
                          : ''
                      }
                    />
                  </Stack>
                )}

                <Stack
                  gap="1rem"
                  alignItems="center"
                  flexDirection="row"
                  width={'100%'}
                  display={'flex'}
                  justifyContent={'space-between'}
                >
                  <IconButton
                    sx={{
                      width: '32px',
                      height: '32px',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #F1F1F1',
                      background: 'rgba(99, 102, 241, 0.10)',
                      '&:hover': {
                        background: 'rgba(99, 102, 241, 0.10)',
                      },
                    }}
                    disabled={currentIndex === 0}
                    onClick={handlePrevious}
                  >
                    <NavigateBefore sx={{ color: '#6366F1', fontSize: '14px' }} />
                  </IconButton>
                  <Stack>
                    <Typography
                      sx={{
                        color: 'rgba(99, 102, 241, 0.60)',
                        textAlign: 'center',
                        fontFamily: 'Open Sans',
                        fontSize: '15px',
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: 'normal',
                      }}
                    >
                      {currentIndex + 1}/{announcements?.length}
                    </Typography>
                  </Stack>
                  <IconButton
                    onClick={handleNext}
                    disabled={currentIndex === announcements?.length - 1}
                    sx={{
                      width: '32px',
                      height: '32px',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #F1F1F1',
                      background: 'rgba(99, 102, 241, 0.10)',
                      '&:hover': {
                        background: 'rgba(99, 102, 241, 0.10)',
                      },
                    }}
                  >
                    <NavigateNext sx={{ color: '#6366F1', fontSize: '14px' }} />
                  </IconButton>
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {currentIndex ? (
            <IconButton
              onClick={() => handleOpenDeleteAnnounceModal(selectedAnnouncement?.uuid)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '11.765px',
                background: 'var(--error-light, #FEE4E2)',
              }}
            >
              <TrashIcon />
            </IconButton>
          ) : (
            <div></div>
          )}

          <Box
            sx={{
              display: 'flex',
              gap: '10px',
            }}
          >
            {
              <Button
                autoFocus
                variant="text"
                onClick={handleCloseDialog}
              >
                {'Cancel'}
              </Button>
            }
            <Button
              autoFocus
              variant="contained"
              type="submit"
              disabled={
                !selectedAnnouncement?.announce || selectedAnnouncement?.announce?.length > 400
              }
              onClick={handleSaveAnnouncement}
            >
              {currentIndex ? (mode === 'VIEW' ? 'Edit' : 'Save') : 'Announce'}
            </Button>
          </Box>
        </DialogActions>
      </Box>

      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </>
  );
};

export default AnnouncementDialog;
